import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { usersApi, authApi } from '../services/api';
import { toast } from 'sonner';
import { Users, Plus, Edit, Trash2, Loader2, KeyRound, User, Lock, Unlock, Shield, Crown } from 'lucide-react';

const UsersPage = () => {
  const { admin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [createForm, setCreateForm] = useState({
    email: '', password: '', name: '',
    permissions: { max_transfers_per_day: -1, max_emails_per_day: -1, max_transfers_per_month: -1, max_emails_per_month: -1, can_create_transfers: true, can_execute_transfers: true, can_reject_transfers: true, can_send_emails: true, can_view_audit_logs: false }
  });
  const [editForm, setEditForm] = useState({ name: '', permissions: {}, is_blocked: false });
  const [newPassword, setNewPassword] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await usersApi.getAll();
      setUsers(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await usersApi.create({ ...createForm, role: 'user' });
      toast.success('Utilisateur cree avec succes');
      setShowCreateModal(false);
      setCreateForm({
        email: '', password: '', name: '',
        permissions: { max_transfers_per_day: -1, max_emails_per_day: -1, max_transfers_per_month: -1, max_emails_per_month: -1, can_create_transfers: true, can_execute_transfers: true, can_reject_transfers: true, can_send_emails: true, can_view_audit_logs: false }
      });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await usersApi.update(selectedUser.id, editForm);
      toast.success('Utilisateur mis a jour');
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleBlock = async (user) => {
    try {
      await usersApi.update(user.id, { is_blocked: !user.is_blocked });
      toast.success(user.is_blocked ? 'Compte reactive' : 'Compte bloque');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleTogglePremium = async (user) => {
    const newType = user.account_type === 'premium' ? 'free' : 'premium';
    try {
      await usersApi.update(user.id, { account_type: newType });
      toast.success(newType === 'premium' ? 'Compte passe en Premium' : 'Compte passe en Free');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await usersApi.delete(userId);
      toast.success('Utilisateur supprime');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await authApi.resetPassword({ user_id: selectedUser.id, new_password: newPassword });
      toast.success('Mot de passe reinitialise');
      setShowResetPasswordModal(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      is_blocked: user.is_blocked || false,
      permissions: user.permissions || { max_transfers_per_day: -1, max_emails_per_day: -1, max_transfers_per_month: -1, max_emails_per_month: -1, can_create_transfers: true, can_execute_transfers: true, can_reject_transfers: true, can_send_emails: true, can_view_audit_logs: false }
    });
    setShowEditModal(true);
  };

  const openResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  return (
    <Layout>
      <div className="max-w-4xl" data-testid="users-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl text-slate-900">Gestion des utilisateurs</h1>
            <p className="text-slate-500 text-sm mt-1">{users.length} utilisateur(s)</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto bg-[#0066FF] hover:bg-[#0052CC] rounded-lg" data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvel utilisateur
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0066FF]" /></div>
        ) : users.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-500">Aucun utilisateur. Cliquez sur "Nouvel utilisateur" pour en creer un.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {users.map((user) => (
              <Card key={user.id} data-testid={`user-card-${user.id}`} className={`rounded-xl border-slate-200 shadow-sm ${user.is_blocked ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${user.is_blocked ? 'bg-red-100' : 'bg-slate-100'}`}>
                      {user.is_blocked ? <Lock className="w-5 h-5 text-red-500" /> : <User className="w-5 h-5 text-slate-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-900 text-sm">{user.name}</p>
                        <Badge className="bg-slate-100 text-slate-700 text-[10px]">Utilisateur</Badge>
                        {user.account_type === 'premium' ? (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] border border-amber-200 flex items-center gap-1"><Crown className="w-3 h-3" />Compte Premium</Badge>
                        ) : (
                          <Badge className="bg-slate-50 text-slate-400 text-[10px]">Compte Standard</Badge>
                        )}
                        {user.is_blocked && <Badge className="bg-red-100 text-red-700 text-[10px]">Bloque</Badge>}
                        {user.totp_enabled && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">2FA</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      <div className="flex flex-wrap gap-3 mt-1">
                        {user.permissions?.max_transfers_per_day >= 0 && (
                          <span className="text-xs text-slate-400">Virements: {user.permissions.max_transfers_per_day}/jour</span>
                        )}
                        {user.permissions?.max_transfers_per_month >= 0 && (
                          <span className="text-xs text-slate-400">Virements: {user.permissions.max_transfers_per_month}/mois</span>
                        )}
                        {user.permissions?.max_emails_per_day >= 0 && (
                          <span className="text-xs text-slate-400">Emails: {user.permissions.max_emails_per_day}/jour</span>
                        )}
                        {user.permissions?.max_emails_per_month >= 0 && (
                          <span className="text-xs text-slate-400">Emails: {user.permissions.max_emails_per_month}/mois</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(user)} title="Modifier" data-testid={`edit-user-${user.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openResetPassword(user)} title="Reinitialiser le mot de passe" data-testid={`reset-pwd-${user.id}`}>
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleTogglePremium(user)}
                      title={user.account_type === 'premium' ? 'Passer en Free' : 'Passer en Premium'}
                      className={user.account_type === 'premium' ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50'}
                      data-testid={`toggle-premium-${user.id}`}
                    >
                      <Crown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleToggleBlock(user)}
                      title={user.is_blocked ? 'Reactiver' : 'Bloquer'}
                      className={user.is_blocked ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'}
                      data-testid={`toggle-block-${user.id}`}
                    >
                      {user.is_blocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(user.id)} title="Supprimer" data-testid={`delete-user-${user.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create User Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouvel utilisateur</DialogTitle>
              <DialogDescription>Creer un nouveau compte utilisateur</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Nom complet</Label>
                  <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required data-testid="create-user-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required data-testid="create-user-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Mot de passe</Label>
                <Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required data-testid="create-user-password" />
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Limites</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Virements max / jour (-1 = illimite)</Label>
                    <Input type="number" min={-1} value={createForm.permissions.max_transfers_per_day} onChange={(e) => setCreateForm({ ...createForm, permissions: { ...createForm.permissions, max_transfers_per_day: parseInt(e.target.value) || -1 } })} data-testid="create-user-max-transfers" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Emails max / jour (-1 = illimite)</Label>
                    <Input type="number" min={-1} value={createForm.permissions.max_emails_per_day} onChange={(e) => setCreateForm({ ...createForm, permissions: { ...createForm.permissions, max_emails_per_day: parseInt(e.target.value) || -1 } })} data-testid="create-user-max-emails" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Virements max / mois (-1 = illimite)</Label>
                    <Input type="number" min={-1} value={createForm.permissions.max_transfers_per_month} onChange={(e) => setCreateForm({ ...createForm, permissions: { ...createForm.permissions, max_transfers_per_month: parseInt(e.target.value) || -1 } })} data-testid="create-user-max-transfers-month" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Emails max / mois (-1 = illimite)</Label>
                    <Input type="number" min={-1} value={createForm.permissions.max_emails_per_month} onChange={(e) => setCreateForm({ ...createForm, permissions: { ...createForm.permissions, max_emails_per_month: parseInt(e.target.value) || -1 } })} data-testid="create-user-max-emails-month" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)}>Annuler</Button>
                <Button type="submit" disabled={actionLoading} data-testid="create-user-submit">
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Creer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier l'utilisateur</DialogTitle>
              <DialogDescription>{selectedUser?.email}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom complet</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" />
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Permissions et limites</p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Virements max / jour (-1 = illimite)</Label>
                    <Input type="number" min={-1} value={editForm.permissions?.max_transfers_per_day ?? -1} onChange={(e) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, max_transfers_per_day: parseInt(e.target.value) || -1 } })} data-testid="edit-user-max-transfers" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Emails max / jour (-1 = illimite)</Label>
                    <Input type="number" min={-1} value={editForm.permissions?.max_emails_per_day ?? -1} onChange={(e) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, max_emails_per_day: parseInt(e.target.value) || -1 } })} data-testid="edit-user-max-emails" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Virements max / mois (-1 = illimite)</Label>
                    <Input type="number" min={-1} value={editForm.permissions?.max_transfers_per_month ?? -1} onChange={(e) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, max_transfers_per_month: parseInt(e.target.value) || -1 } })} data-testid="edit-user-max-transfers-month" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Emails max / mois (-1 = illimite)</Label>
                    <Input type="number" min={-1} value={editForm.permissions?.max_emails_per_month ?? -1} onChange={(e) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, max_emails_per_month: parseInt(e.target.value) || -1 } })} data-testid="edit-user-max-emails-month" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Creer des virements</Label>
                    <Switch checked={editForm.permissions?.can_create_transfers ?? true} onCheckedChange={(v) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, can_create_transfers: v } })} data-testid="edit-perm-create" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Executer des virements</Label>
                    <Switch checked={editForm.permissions?.can_execute_transfers ?? true} onCheckedChange={(v) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, can_execute_transfers: v } })} data-testid="edit-perm-execute" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Rejeter des virements</Label>
                    <Switch checked={editForm.permissions?.can_reject_transfers ?? true} onCheckedChange={(v) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, can_reject_transfers: v } })} data-testid="edit-perm-reject" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Envoyer des emails</Label>
                    <Switch checked={editForm.permissions?.can_send_emails ?? true} onCheckedChange={(v) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, can_send_emails: v } })} data-testid="edit-perm-emails" />
                  </div>
                </div>
              </div>
              <div className="border-t pt-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Statut du compte</p>
                  <p className="text-xs text-slate-500">{editForm.is_blocked ? 'Le compte est bloque' : 'Le compte est actif'}</p>
                </div>
                <Switch checked={editForm.is_blocked} onCheckedChange={(v) => setEditForm({ ...editForm, is_blocked: v })} data-testid="edit-user-blocked" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setShowEditModal(false)}>Annuler</Button>
                <Button type="submit" disabled={actionLoading} data-testid="edit-user-submit">
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Enregistrer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reset Password Modal */}
        <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reinitialiser le mot de passe</DialogTitle>
              <DialogDescription>{selectedUser?.email}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Nouveau mot de passe</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="reset-password-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setShowResetPasswordModal(false)}>Annuler</Button>
                <Button type="submit" disabled={actionLoading} data-testid="reset-password-submit">
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Reinitialiser
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
